import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CURRENCIES, useCurrency } from '@/lib/currency';

// Minimalist currency dropdown, styled to match the time-horizon selector.
// Uses Radix primitives directly so the trigger can show a compact "SYM CODE"
// while each menu item shows the full currency name alongside it.
export default function CurrencySelect({ className = '' }) {
  const [code, setCode] = useCurrency();

  return (
    <SelectPrimitive.Root value={code} onValueChange={setCode}>
      <SelectPrimitive.Trigger
        data-testid="currency-select"
        className={cn(
          'flex h-9 w-[78px] sm:w-[88px] items-center justify-between whitespace-nowrap',
          'rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
          'ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          '[&>span]:line-clamp-1',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder="USD" />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'relative z-50 max-h-[320px] min-w-[15rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {CURRENCIES.map((c) => (
              <SelectPrimitive.Item
                key={c.code}
                value={c.code}
                data-testid={`currency-option-${c.code}`}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center gap-2',
                  'rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                  'focus:bg-accent focus:text-accent-foreground',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="h-4 w-4" />
                  </SelectPrimitive.ItemIndicator>
                </span>
                {/* Menu-only symbol (outside ItemText, so not reflected in trigger). */}
                <span className="font-mono text-muted-foreground w-6 text-left text-xs flex-shrink-0">
                  {c.symbol}
                </span>
                {/* ItemText contents are what the trigger reflects when selected.
                    Keep it compact: only the 3-letter ISO code. */}
                <SelectPrimitive.ItemText>
                  <span className="font-medium">{c.code}</span>
                </SelectPrimitive.ItemText>
                {/* Menu-only extra: full currency name (does NOT appear in trigger). */}
                <span className="ml-auto text-muted-foreground text-xs truncate">
                  {c.name}
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
